import { faFileExcel } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { UIViewInjectedProps } from '@uirouter/react';
import { Alert, Button, Checkbox, Empty, Spin, Table, Tabs } from 'antd';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import {
  PERMISSION_OBJ_COOPERATIVE,
  PERMISSION_OBJ_FARM,
  PERMISSION_OBJ_FARMER,
  PERMISSION_OBJ_WAYBILL,
  PERMISSION_READ,
} from '@/constants/permissions';
import { authStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { showError } from '@/utils/common';

const { TabPane } = Tabs;

type Section = cyrian.api.lots.LotExportSection;

const ALL_SECTIONS: Section[] = ['cooperatives', 'farmers', 'polygons', 'waybills'];

// Keyed by each section's primary permission object — the backend omits a section
// the user can't Read, so its checkbox must not be offered either.
const SECTION_READ_PERMISSION: Record<Section, [string, number][]> = {
  cooperatives: [[PERMISSION_OBJ_COOPERATIVE, PERMISSION_READ]],
  farmers: [[PERMISSION_OBJ_FARMER, PERMISSION_READ]],
  polygons: [[PERMISSION_OBJ_FARM, PERMISSION_READ]],
  waybills: [[PERMISSION_OBJ_WAYBILL, PERMISSION_READ]],
};

const dash = (value: unknown) =>
  value === null || value === undefined || value === '' ? '—' : (value as React.ReactNode);

const summaryColumns = [
  { title: <FormattedMessage id="lots.export.col.lot" />, dataIndex: 'lot' },
  { title: <FormattedMessage id="lots.export.col.product" />, dataIndex: 'product', render: dash },
  {
    title: <FormattedMessage id="lots.export.col.cooperatives-count" />,
    dataIndex: 'cooperativesCount',
    render: dash,
  },
  { title: <FormattedMessage id="lots.export.col.farmers-count" />, dataIndex: 'farmersCount', render: dash },
  { title: <FormattedMessage id="lots.export.col.lot-weight" />, dataIndex: 'lotWeight', render: dash },
  {
    title: <FormattedMessage id="lots.export.col.polygon-coverage" />,
    dataIndex: 'polygonCoverage',
    render: dash,
  },
];

const cooperativeColumns = [
  { title: <FormattedMessage id="lots.export.col.lot" />, dataIndex: 'lot' },
  { title: <FormattedMessage id="lots.export.col.cooperative" />, dataIndex: 'cooperative', render: dash },
  { title: <FormattedMessage id="lots.export.col.code" />, dataIndex: 'code', render: dash },
  { title: <FormattedMessage id="lots.export.col.consortium" />, dataIndex: 'consortium', render: dash },
  { title: <FormattedMessage id="lots.export.col.deliveries" />, dataIndex: 'deliveries', render: dash },
  { title: <FormattedMessage id="lots.export.col.supplied-kg" />, dataIndex: 'suppliedKg', render: dash },
  { title: <FormattedMessage id="lots.export.col.bags" />, dataIndex: 'bags', render: dash },
];

const farmerColumns = [
  { title: <FormattedMessage id="lots.export.col.lot" />, dataIndex: 'lot' },
  { title: <FormattedMessage id="lots.export.col.cooperative" />, dataIndex: 'cooperative', render: dash },
  { title: <FormattedMessage id="lots.export.col.farmer" />, dataIndex: 'farmer', render: dash },
  { title: <FormattedMessage id="lots.export.col.farmer-id" />, dataIndex: 'farmerId', render: dash },
  { title: <FormattedMessage id="lots.export.col.delivery" />, dataIndex: 'delivery', render: dash },
  { title: <FormattedMessage id="lots.export.col.volume-kg" />, dataIndex: 'volumeKg', render: dash },
  { title: <FormattedMessage id="lots.export.col.bags" />, dataIndex: 'bags', render: dash },
];

const polygonColumns = [
  { title: <FormattedMessage id="lots.export.col.lot" />, dataIndex: 'lot' },
  { title: <FormattedMessage id="lots.export.col.plots-total" />, dataIndex: 'plotsTotal', render: dash },
  { title: <FormattedMessage id="lots.export.col.with-polygon" />, dataIndex: 'withPolygon', render: dash },
  {
    title: <FormattedMessage id="lots.export.col.coverage-percent" />,
    dataIndex: 'coveragePercent',
    render: dash,
  },
];

const waybillColumns = [
  { title: <FormattedMessage id="lots.export.col.lot" />, dataIndex: 'lot' },
  { title: <FormattedMessage id="lots.export.col.waybill" />, dataIndex: 'waybill', render: dash },
  { title: <FormattedMessage id="lots.export.col.product" />, dataIndex: 'product', render: dash },
  { title: <FormattedMessage id="lots.export.col.cooperative" />, dataIndex: 'cooperative', render: dash },
  { title: <FormattedMessage id="lots.export.col.campaign" />, dataIndex: 'campaign', render: dash },
  {
    title: <FormattedMessage id="lots.export.col.declared-net-weight-kg" />,
    dataIndex: 'declaredNetWeightKg',
    render: dash,
  },
  { title: <FormattedMessage id="lots.export.col.bags" />, dataIndex: 'bags', render: dash },
];

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  link.click();
  window.URL.revokeObjectURL(url);
};

export const LotExportReport = ({ transition }: UIViewInjectedProps) => {
  // Selection is a fixed input to this screen: capture it once so the report-load effect
  // has a stable dependency and can't re-fire on every render.
  const [selectedLots] = useState<number[]>(() => transition.targetState().params().selectedLots ?? []);

  const availableSections = ALL_SECTIONS.filter((section) =>
    authStore.hasPermissions(SECTION_READ_PERMISSION[section]),
  );
  const [sections, setSections] = useState<Section[]>(availableSections);
  const [report, setReport] = useState<cyrian.api.lots.LotExportReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadReport = useCallback(() => {
    if (!selectedLots.length) {
      return;
    }
    setLoading(true);
    api.lots
      .exportReport({ lotIds: selectedLots, sections })
      .source.then(({ data }) => setReport(data))
      .catch(showError)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLots, sections]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const onExport = () => {
    setExporting(true);
    api.lots
      .exportXlsx({ lotIds: selectedLots, sections })
      .source.then((blob) => downloadBlob(blob, `lots-export-${selectedLots.length}.xlsx`))
      .catch(showError)
      .finally(() => setExporting(false));
  };

  const onClose = () => {
    transition.router.stateService.go('base-layout.lots');
  };

  // [] is valid: the backend reads an empty sections array as "summary only".
  const onSectionsChange = (checked: any[]) => setSections(checked as Section[]);

  const polygons = report?.polygons ?? [];
  const polygonBanner = polygons.length ? (
    <Alert
      type="info"
      showIcon={true}
      style={{ marginBottom: 16 }}
      message={getTranslatedString('lots.export.polygon-banner', {
        0: polygons.reduce((acc, row) => acc + row.withPolygon, 0),
        1: polygons.reduce((acc, row) => acc + row.plotsTotal, 0),
      })}
    />
  ) : null;

  const extra = (
    <Button
      type="primary"
      icon={<FontAwesomeIcon icon={faFileExcel} />}
      loading={exporting}
      disabled={!selectedLots.length || loading}
      onClick={onExport}
    >
      &nbsp;&nbsp;
      <FormattedMessage id="lots.export.download" />
    </Button>
  );

  return (
    <Drawer
      onClose={onClose}
      visible={true}
      width="80%"
      title={getTranslatedString('lots.export.title', { 0: selectedLots.length })}
      extra={extra}
    >
      {!selectedLots.length ? (
        <Empty description={getTranslatedString('lots.export.no-selection')} />
      ) : (
        <>
          {availableSections.length > 0 && (
            <Checkbox.Group
              style={{ marginBottom: 16 }}
              value={sections}
              onChange={onSectionsChange}
              options={availableSections.map((value) => ({
                label: getTranslatedString(`lots.export.section.${value}`),
                value,
              }))}
            />
          )}
          <Spin spinning={loading}>
            {report && (
              <Tabs>
                <TabPane tab={<FormattedMessage id="lots.export.tab.summary" />} key="summary">
                  <Table
                    rowKey="lot"
                    size="small"
                    columns={summaryColumns}
                    dataSource={report.summary}
                    pagination={false}
                  />
                </TabPane>
                {report.cooperatives && (
                  <TabPane tab={<FormattedMessage id="lots.export.tab.cooperatives" />} key="cooperatives">
                    <Table
                      rowKey={(row, index) => `${row.lot}-${row.code}-${index}`}
                      size="small"
                      columns={cooperativeColumns}
                      dataSource={report.cooperatives}
                    />
                  </TabPane>
                )}
                {report.farmers && (
                  <TabPane tab={<FormattedMessage id="lots.export.tab.farmers" />} key="farmers">
                    <Table
                      rowKey={(row, index) => `${row.lot}-${row.farmerId}-${index}`}
                      size="small"
                      columns={farmerColumns}
                      dataSource={report.farmers}
                    />
                  </TabPane>
                )}
                {report.polygons && (
                  <TabPane tab={<FormattedMessage id="lots.export.tab.polygons" />} key="polygons">
                    {polygonBanner}
                    <Table
                      rowKey="lot"
                      size="small"
                      columns={polygonColumns}
                      dataSource={report.polygons}
                      pagination={false}
                    />
                  </TabPane>
                )}
                {report.waybills && (
                  <TabPane tab={<FormattedMessage id="lots.export.tab.waybills" />} key="waybills">
                    <Table
                      rowKey={(row, index) => `${row.lot}-${row.waybill}-${index}`}
                      size="small"
                      columns={waybillColumns}
                      dataSource={report.waybills}
                    />
                  </TabPane>
                )}
              </Tabs>
            )}
          </Spin>
        </>
      )}
    </Drawer>
  );
};
